import { Shield, Target, Eye, Award, Users, TrendingUp } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const About = () => {
  return (
    <div className="mt-20">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#15448A] via-[#1e5aa8] to-[#0d2f5e] text-white py-24">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="bg-[#F47A20]/20 text-white hover:bg-[#F47A20]/30">
              Sobre a LUVCORP
            </Badge>
            <h1 className="text-5xl font-bold">
              Quem Somos
            </h1>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto space-y-8 text-lg leading-relaxed text-gray-700">
            <p>
              A <span className="font-bold text-[#15448A]">LUVCORP</span> nasceu pequena. Começou com serviços simples, rápidos, quase silenciosos, dentro de um condomínio como tantos outros. Mas, desde o primeiro dia, nunca foi apenas sobre executar um serviço. Sempre foi sobre <span className="font-semibold text-[#F47A20]">cuidar</span>.
            </p>

            <p>
              Cuidar de lugares que carregam histórias. Lugares que representam anos de esforço, renúncias e conquistas. Porque, para cada morador, aquele condomínio não é apenas concreto, pintura ou fachada — é o sonho de uma vida inteira que finalmente ganhou forma.
            </p>

            <p>
              Foi compreendendo esse valor que a LUVCORP construiu seu propósito. Cada obra, por menor que fosse, sempre foi tratada como algo grande. Cada reparo carregava a responsabilidade de preservar um patrimônio que representa segurança, pertencimento e futuro para quem vive ali.
            </p>

            <div className="bg-gradient-to-r from-[#15448A]/5 to-[#F47A20]/5 p-8 rounded-2xl border-l-4 border-[#F47A20] my-12">
              <p className="text-xl font-semibold text-gray-900 italic">
                "Com o tempo, a LUVCORP cresceu. Mas cresceu sem perder sua essência."
              </p>
            </div>

            <p>
              Cresceu com planejamento, critério técnico e a consciência de que não existe reforma de qualidade sem respeito às pessoas envolvidas. Por isso, atuamos de forma empática, entendendo a realidade, as necessidades e as expectativas de cada condomínio, adaptando soluções e construindo caminhos viáveis, seguros e duradouros.
            </p>

            <p>
              Nossa entrega vai além do padrão porque acreditamos que quem cuida de sonhos não pode trabalhar no automático. Prezamos por <span className="font-semibold text-[#15448A]">reformas de altíssima qualidade</span>, mão de obra qualificada, processos rigorosos e acompanhamento técnico constante. Cada detalhe importa quando o que está em jogo é o patrimônio de alguém.
            </p>

            <p>
              A LUVCORP é uma empresa guiada por valores claros e princípios inegociáveis. Nossa cultura organizacional é forte, estruturada e disciplinada. A hierarquia, a organização e o senso de responsabilidade <span className="font-semibold text-[#15448A]">herdados do Exército Militar Brasileiro</span> fazem parte do nosso dia a dia, refletindo-se em um regramento interno bem definido, seguido com rigor por toda a equipe.
            </p>

            <p>
              Esse padrão de exigência começa na liderança e se reflete em cada etapa do trabalho: na escolha dos materiais, na qualificação da mão de obra, nos métodos construtivos e no cuidado com a execução. <span className="font-semibold text-[#F47A20]">Exigimos qualidade porque entregamos qualidade.</span>
            </p>

            <p>
              Nossa história é marcada por um crescimento progressivo, saudável e estruturado. Conquistamos espaço no mercado passo a passo, sem atalhos, mantendo o mesmo compromisso com capricho, dedicação e responsabilidade em qualquer serviço, independentemente do seu porte.
            </p>

            <div className="bg-gradient-to-br from-[#15448A] to-[#0d2f5e] p-10 rounded-2xl text-white my-12 text-center">
              <p className="text-2xl font-bold mb-4">
                A LUVCORP existe para cuidar do que realmente importa.
              </p>
              <p className="text-lg text-blue-100">
                Para preservar patrimônios que são, antes de tudo, sonhos. E para entregar obras que resistem ao tempo — não apenas na estrutura, mas na confiança construída com cada cliente.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-gradient-to-br from-[#15448A] to-[#0d2f5e] text-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold mb-2">45+</div>
              <div className="text-blue-100 text-lg">Condomínios Atendidos</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">5+</div>
              <div className="text-blue-100 text-lg">Anos de Experiência</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">95%</div>
              <div className="text-blue-100 text-lg">Satisfação dos Clientes</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">100%</div>
              <div className="text-blue-100 text-lg">Equipe Própria</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl font-bold">
              Pronto para Conhecer Nosso Trabalho?
            </h2>
            <p className="text-xl text-gray-600">
              Entre em contato e descubra como podemos transformar seu condomínio.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contato">
                <Button size="lg" className="bg-[#F47A20] hover:bg-[#e66a10] text-white shadow-lg text-lg px-10 py-7">
                  Solicitar Orçamento
                </Button>
              </Link>
              <Link to="/projetos">
                <Button size="lg" variant="outline" className="border-2 border-[#15448A] text-[#15448A] hover:bg-[#15448A]/5 text-lg px-10 py-7">
                  Ver Condomínios
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
